<!-- List users in the workspace -->
<template>
  <div>
    <b-modal
      v-model="addUserModal"
      title="Add User"
      ok-title="Add"
      ok-variant="primary"
      cancel-variant="outline-primary"
      cancel-title="Cancel"
      @ok="addUser"
    >
      <b-form @submit.stop.prevent="addUser">
        <b-form-group
          label="Email"
          label-for="add-user-email"
          :state="emailState"
        >
          <b-form-input
            id="add-user-email"
            v-model="email"
            :state="emailState"
            required
            placeholder="Enter email"
          />
          <b-form-invalid-feedback :state="emailState">
            Please enter a valid email address.
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-group
          label="Role"
          label-for="add-user-role"
          :state="roleState"
        >
          <v-select
            id="add-user-role"
            v-model="role"
            :state="roleState"
            :options="roles"
            :reduce="role => role.value"
            label="text"
            required
            placeholder="Select role"
          />
          <b-form-invalid-feedback :state="roleState">
            Please select a role.
          </b-form-invalid-feedback>
        </b-form-group>
      </b-form>
    </b-modal>
    <b-modal
      v-model="removeUserModal"
      title="Remove User"
      ok-title="Remove"
      ok-variant="danger"
      cancel-title="Cancel"
      cancel-variant="outline-primary"
      @ok="removeUserFromWorkspace"
    >
      <p
        v-if="userToRemove !== null"
      >{{ `Are you sure you want to remove ${userToRemove.Attributes.find(attr => attr.Name === 'email').Value} from your workspace?` }}</p>
    </b-modal>

    <b-card
      class="mb-2"
    >
      <!-- workspace tokens -->
      <b-row class="border-bottom mb-2">
        <b-col
          cols="10"
        >
          <h4>Workspace Users</h4>
          <p>These users have access to your PRYM Workspace</p>
        </b-col>
        <b-col>
          <b-button
            variant="outline-primary"

            class="float-right mb-1"
            @click="addUserModal = true"
          >
            Add User
          </b-button>
        </b-col>
      </b-row>
      <div v-if="loading">
        <p>Loading...</p>
      </div>
      <div v-else-if="users.length === 0">
        <p>No users found.</p>
      </div>
      <div v-else>
        <b-row class="mb-2">
          <b-col>
            <b-form-group
              label="Filter"
              label-for="filter"
            >
              <b-form-input
                id="filter"
                v-model="filter"
                placeholder="Search by email"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-table
          striped
          small
          :items="users"
          :fields="fields"
          :filter="filter"
          :filter-included-fields="filterOn"
          @filtered="onFiltered"
        >
          <template #cell(email)="row">
            <div v-if="row.item.email">
              {{ row.item.email }}
            </div>
            <div v-else>
              <template v-if="loading">
                Loading...
              </template>
              <template v-else>
                {{ getEmail(row.item) }}
              </template>
            </div>
          </template>
          <template #cell(role)="row">
            <b-badge
              v-if="row.item.role === 'admin'"
              variant="danger"
            >
              Admin
            </b-badge>
            <b-badge
              v-else-if="row.item.role === 'editor'"
              variant="warning"
            >
              Editor
            </b-badge>
            <b-badge
              v-else
              variant="success"
            >
              Viewer
            </b-badge>
          </template>
          <template #cell(status)="row">
            <b-badge
              v-if="row.item.UserStatus === 'CONFIRMED'"
              variant="success"
            >
              Confirmed
            </b-badge>
            <b-badge
              v-else
              variant="warning"
            >
              {{ row.item.UserStatus }}
            </b-badge>
          </template>
          <!-- eslint-disable-next-line vue/no-unused-vars -->
          <template #cell(actions)="data">
            <b-button
              variant="outline-danger"
              size="sm"
              @click="removeUserModal = true; userToRemove = data.item"
            >
              Remove
            </b-button>
          </template>
        </b-table>
      </div>
    </b-card>
  </div>

</template>

<script>

import { Auth, API } from 'aws-amplify'
import {
  BButton,
  BModal,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BTable,
  BBadge,
  BCard,
  BRow,
  BCol,
} from 'bootstrap-vue'
import vSelect from 'vue-select'

export default {
  components: {
    BButton,
    BModal,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    vSelect,
    BTable,
    BBadge,
    BCard,
    BRow,
    BCol,
  },
  data() {
    return {
      addUserModal: false,
      removeUserModal: false,
      userToRemove: null,
      users: [],
      fields: [
        { key: 'email', label: 'Email', sortable: true },
        { key: 'role', label: 'Role', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'actions', label: 'Actions' },
      ],
      filter: null,
      filterOn: [],
      email: '',
      emailState: null,
      role: 'viewer',
      roleState: null,
      roles: [
        { value: 'viewer', text: 'Viewer' },
        { value: 'editor', text: 'Editor' },
        { value: 'admin', text: 'Admin' },
      ],
      workspace: JSON.parse(localStorage.getItem('workspace')),
      loading: false,
    }
  },
  created() {
    this.listUsers()
  },
  methods: {
    async addUser() {
      // if successful, refresh page and show toast
      try {
        this.loading = true
        const workspace = await JSON.parse(localStorage.getItem('workspace'))
        const apiName = 'AdminQueries'
        const path = '/addUserToGroup'
        const myInit = {
          body: {
            username: this.email,
            groupname: workspace.id,
            role: this.role,
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`,
          },
        }
        const rest = await API.post(apiName, path, myInit)
        this.addUserModal = false

        // create a toastification
        this.$bvToast.toast('User added successfully', {
          title: `${rest.username} added to workspace`,
          variant: 'success',
          solid: true,
        })
        this.listUsers()
        this.loading = false
      } catch (error) {
        this.loading = false
        this.$bvToast.toast('Error adding user', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      }
    },
    async removeUserFromWorkspace() {
      try {
        const workspace = await JSON.parse(localStorage.getItem('workspace'))
        // eslint-disable-next-line no-unused-vars
        const apiName = 'AdminQueries'
        // eslint-disable-next-line no-unused-vars
        const path = '/removeUserFromGroup'
        // eslint-disable-next-line no-unused-vars
        const myInit = {
          body: {
            username: this.userToRemove.Username,
            groupname: workspace.id,
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`,
          },
        }

        // eslint-disable-next-line no-unused-vars
        const rest = await API.post(apiName, path, myInit)
        this.deleteUserModal = false
        // create a toastification
        const email = this.userToRemove.Attributes.find(attr => attr.Name === 'email').Value
        this.$bvToast.toast('User removed successfully', {
          // eslint-disable-next-line no-undef
          title: `${email} removed from workspace`,
          variant: 'success',
          solid: true,
        })
        this.listUsers()
      } catch (error) {
        // console.log(error)
      }
    },
    async listUsers() {
      const workspace = await JSON.parse(localStorage.getItem('workspace'))
      this.loading = true
      try {
        const apiName = 'AdminQueries'
        const path = '/listUsersInGroup'
        const myInit = {
          queryStringParameters: {
            groupname: workspace.id,
          },
          headers: {
            'Content-Type': 'application/json',
            Authorization: `${(await Auth.currentSession()).getAccessToken().getJwtToken()}`,
          },
        }
        const response = await API.get(apiName, path, myInit)
        // There is duplicate users only keep the ones with the highest role. groupname has the role in it so we can sort by that
        const uniqueUsers = response.reduce((acc, user) => {
          if (!acc.find(u => u.Username === user.Username)) {
            acc.push({ ...user, role: user.groupname.split('.')[1] })
          }
          return acc
        }, [])
        this.loading = false
        this.users = uniqueUsers
      } catch (error) {
        // console.log(error)
      }
    },
    getEmail(rowItem) {
      let email = 'No email'
      rowItem.Attributes.forEach(attribute => {
        if (attribute.Name === 'email') {
          email = attribute.Value
        }
      })
      return email
    },
    onFiltered(filteredItems) {
      // Trigger pagination to update the number of buttons/pages due to filtering
      this.totalRows = filteredItems.length
      this.currentPage = 1
    },
  },
}
</script>

<style lang="scss">

  @import '@core/scss/vue/libs/vue-select.scss';
</style>
